import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, SHOW_PRICING } from '../App';
import { motion } from 'framer-motion';
import { Globe, Sparkles, Compass, Eye, Zap, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';
import BetaSpotsCounter from '../components/BetaSpotsCounter';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'pt', name: 'Português' },
  { code: 'ar', name: 'العربية' },
];

const IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_brand-alchemy-2/artifacts/juxcc577_IMG_0815.jpeg',
  journey1: 'https://customer-assets.emergentagent.com/job_brand-alchemy-2/artifacts/mmbdaeck_IMG_0816.jpeg',
  privateBeta: 'https://customer-assets.emergentagent.com/job_brand-alchemy-2/artifacts/bz2kfi3u_IMG_0822.jpeg',
  clarity: 'https://customer-assets.emergentagent.com/job_brand-alchemy-2/artifacts/tarelmoc_IMG_1217.jpeg',
};

const HEADER_HEIGHT = 90;

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const hideWatermark = () => {
      document.querySelectorAll('[class*="emergent"], [class*="watermark"], [class*="Made"], a[href*="emergent"]').forEach(el => {
        if (el) el.style.display = 'none';
      });
    };
    hideWatermark();
    const interval = setInterval(hideWatermark, 500);
    return () => clearInterval(interval);
  }, []);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    document.documentElement.dir = langCode === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = langCode;
    setShowLangMenu(false);
  };

  const journeySteps = [
    { id: 'clarity', icon: Sparkles, title: t('journey.clarity.title'), emotion: t('journey.clarity.emotion'), tools: [t('journey.clarity.tool1'), t('journey.clarity.tool2')] },
    { id: 'direction', icon: Compass, title: t('journey.direction.title'), emotion: t('journey.direction.emotion'), tools: [t('journey.direction.tool1'), t('journey.direction.tool2'), t('journey.direction.tool3')] },
    { id: 'expression', icon: Eye, title: t('journey.expression.title'), emotion: t('journey.expression.emotion'), tools: [t('journey.expression.tool1'), t('journey.expression.tool2')] },
    { id: 'execution', icon: Zap, title: t('journey.execution.title'), emotion: t('journey.execution.emotion'), tools: [t('journey.execution.tool1')] },
    { id: 'support', icon: Heart, title: t('journey.support.title'), emotion: t('journey.support.emotion'), tools: [t('journey.support.tool1'), t('journey.support.tool2')] }
  ];

  const renderStep = (step, index) => {
    const Icon = step.icon;
    return (
      <motion.div
        key={step.id}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.08 }}
        className="grid md:grid-cols-[60px_1fr] gap-8 items-start"
      >
        <div className="flex md:flex-col items-center gap-3">
          <span className="text-[#D4AF37]/25 font-serif text-xl">{String(index + 1).padStart(2, '0')}</span>
          <div className="w-7 h-7 flex items-center justify-center border border-[#D4AF37]/25">
            <Icon className="text-[#D4AF37]/70" size={12} strokeWidth={1.5} />
          </div>
        </div>
        <div className="border-l border-[#1A1A1A] pl-8">
          <h3 className="font-serif text-base text-white mb-4 tracking-wide">{step.title}</h3>
          <p className="text-[#909090] text-sm font-light italic mb-6 leading-[1.8]">{step.emotion}</p>
          <div className="flex flex-wrap gap-2">
            {step.tools.map((tool, i) => (
              <span key={i} className="text-[#707070] text-[9px] tracking-[0.15em] uppercase border border-[#2A2A2A] px-4 py-2 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-all duration-500">
                {tool}
              </span>
            ))}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      
      {/* ============ FIXED HEADER ============ */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/50 to-transparent" style={{ height: HEADER_HEIGHT }}>
        <div className="h-full max-w-7xl mx-auto px-8 lg:px-16 flex items-center justify-between">
          <Link to="/" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_brand-alchemy-2/artifacts/3hu8wfzt_24FA2A63-8890-4D16-8044-60FA948D15B0.png" 
              alt="Clierxter Vision Lab" 
              className="h-12 w-auto object-contain"
            />
          </Link>
          
          <div className="relative">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="w-8 h-8 flex items-center justify-center border border-white/25 text-white/70 hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-all duration-300"
              data-testid="language-selector"
            >
              <Globe size={14} />
            </button>
            
            {showLangMenu && (
              <div className="absolute top-full right-0 mt-3 bg-[#0A0A0A] border border-[#1A1A1A] py-2 min-w-[130px]">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`w-full px-4 py-2 text-left text-xs hover:bg-[#151515] transition-colors ${currentLang.code === lang.code ? 'text-[#D4AF37]' : 'text-[#909090]'}`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ============ HERO SECTION - LUXURY EDITORIAL TYPOGRAPHY ============ */}
      <section className="relative min-h-screen flex items-end pb-16 md:pb-20">
        {/* Background Image - FACE VISIBLE */}
        <div className="absolute inset-0">
          <img 
            src={IMAGES.hero} 
            alt="" 
            className="w-full h-full object-cover"
            style={{ objectPosition: '50% 0%' }}
          />
          {/* Very subtle overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-transparent" />
        </div>

        {/* Hero Content - LEFT ALIGNED, positioned at bottom */}
        <div className="relative z-10 w-full px-8 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="max-w-2xl"
          >
            {/* LINE 1 - PRIMARY (Large Script T in GOLD + Elegant Serif) - FRENCH DEFAULT - BIGGER */}
            <h1 className="leading-[1.15] mb-5 drop-shadow-lg">
              <span 
                className="text-[#D4AF37] inline-block mr-[-6px]"
                style={{ 
                  fontFamily: "'Pinyon Script', cursive",
                  fontSize: 'clamp(4.5rem, 9vw, 7rem)',
                  lineHeight: '0.8'
                }}
              >
                {t('hero.line1').charAt(0)}
              </span>
              <span 
                className="text-white"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(2rem, 4vw, 3.2rem)',
                  fontWeight: '500'
                }}
              >
                {t('hero.line1').substring(1)}
              </span>
            </h1>
            
            {/* LINE 2 - SECONDARY (Serif with gold accent) */}
            <p 
              className="text-white mb-8 drop-shadow-md"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(1.1rem, 2.5vw, 1.6rem)',
                fontWeight: '400',
                letterSpacing: '0.02em'
              }}
            >
              {t('hero.line2')} <span className="text-[#D4AF37]" style={{ fontWeight: '500' }}>
                {t('hero.alignment')}
              </span>.
            </p>

            {/* LINE 3 - EMOTIONAL (Delicate Italic) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="mb-6"
            >
              <p 
                className="text-white/95 drop-shadow-md"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(1rem, 2vw, 1.35rem)',
                  fontWeight: '400',
                  fontStyle: 'italic',
                  letterSpacing: '0.03em'
                }}
              >
                {t('hero.statement1')}
              </p>
              <p 
                className="text-[#D4AF37] drop-shadow-md"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(1rem, 2vw, 1.35rem)',
                  fontWeight: '400',
                  fontStyle: 'italic',
                  letterSpacing: '0.03em'
                }}
              >
                {t('hero.statement2')}
              </p>
            </motion.div>

            {/* LINE 4 - SUPPORTING (Small caps, READABLE, BOLDER) */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="text-white/90 mb-10 drop-shadow-md"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontSize: '0.8rem',
                fontWeight: '500',
                letterSpacing: '0.25em',
                textTransform: 'uppercase'
              }}
            >
              {t('hero.power')}
            </motion.p>

            {/* CTA - PREMIUM GOLD BUTTON */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="relative"
            >
              {/* Semi-transparent backdrop for better CTA visibility */}
              <div className="absolute inset-0 -m-8 bg-black/30 backdrop-blur-sm rounded-xl -z-10" />
              
              {/* Beta Spots Counter */}
              <div className="mb-6">
                <BetaSpotsCounter variant="minimal" />
              </div>
              
              <Button 
                onClick={() => navigate(user ? '/dashboard' : '/auth')}
                className="bg-gradient-to-r from-[#D4AF37] to-[#C9A227] hover:from-[#E5C585] hover:to-[#D4AF37] text-[#050505] px-14 md:px-20 py-5 md:py-6 tracking-[0.2em] uppercase font-bold transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-[#D4AF37]/40 border border-[#D4AF37]/50"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(0.75rem, 1.2vw, 0.9rem)'
                }}
                data-testid="hero-cta"
              >
                {t('hero.cta')}
              </Button>
              
              <p 
                className="text-white/40 mt-6"
                style={{ 
                  fontSize: '0.6rem',
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase'
                }}
              >
                {t('hero.limited')}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============ TRANSITION SECTION - EDITORIAL ============ */}
      <section className="py-40 text-center bg-[#030303]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="max-w-3xl mx-auto px-8"
        >
          {/* INTRO LINE - French default */}
          <p 
            className="text-white/60 mb-12"
            style={{ 
              fontFamily: "'Playfair Display', serif",
              fontSize: '0.75rem',
              letterSpacing: '0.3em',
              textTransform: 'uppercase'
            }}
          >
            {t('transition.line')}
          </p>
          
          {/* LINE 1 - MAIN TITLE with Script C */}
          <h2 className="leading-[1.2] mb-8 drop-shadow-lg">
            <span 
              className="inline-block mr-[-4px]"
              style={{ 
                fontFamily: "'Pinyon Script', cursive",
                fontSize: 'clamp(3.5rem, 7vw, 5rem)',
                lineHeight: '0.8',
                color: '#D4AF37'
              }}
            >
              C
            </span>
            <span 
              className="text-white"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(1.3rem, 2.8vw, 2rem)',
                fontWeight: '500'
              }}
            >
              {t('transition.line').substring(1)}
            </span>
          </h2>
          
          {/* LINE 2 - SUPPORT TEXT */}
          <p 
            className="text-white/80"
            style={{ 
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(0.95rem, 1.8vw, 1.15rem)',
              fontStyle: 'italic',
              letterSpacing: '0.02em'
            }}
          >
            {t('journey.clarity.emotion')}
          </p>
        </motion.div>
      </section>

      {/* ============ CLARITY EDITORIAL IMAGE ============ */}
      <section className="relative bg-[#030303] pt-12 pb-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative aspect-[4/5] md:aspect-[16/10] overflow-hidden"
          >
            <img 
              src={IMAGES.clarity} 
              alt="" 
              className="w-full h-full object-cover object-top"
            />
          </motion.div>
        </div>
      </section>

      {/* ============ INSIDE VISION LAB ============ */}
      <section className="py-28 bg-[#030303]">
        <div className="max-w-4xl mx-auto px-8 lg:px-16">
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-24"
          >
            <span className="font-accent text-[#D4AF37]/70 text-[8px] tracking-[0.35em] mb-6 block">{t('journey.label')}</span>
            <h2 
              className="text-white tracking-wide"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(1.3rem, 2.5vw, 1.6rem)',
                fontWeight: '500'
              }}
            >
              {t('journey.title')}
            </h2>
          </motion.div>
          
          <div className="space-y-20">
            {/* Clarté Step - Now without image (image is above) */}
            {renderStep(journeySteps[0], 0)}

            {renderStep(journeySteps[1], 1)}
            
            {/* Editorial Break - White Suit Image - FACE FULLY VISIBLE */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="my-28 relative h-[500px] md:h-[650px] overflow-hidden"
            >
              <img src={IMAGES.journey1} alt="" className="w-full h-full object-cover" style={{ objectPosition: '50% 5%' }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-[#030303] opacity-20" />
            </motion.div>
            
            {renderStep(journeySteps[2], 2)}
            {renderStep(journeySteps[3], 3)}
            {renderStep(journeySteps[4], 4)}
          </div>
        </div>
      </section>

      {/* ============ GREEN PORTRAIT SECTION - EDITORIAL LUXURY ============ */}
      <section className="relative h-screen overflow-hidden mt-32">
        {/* Image - Show chin and neck area, face at top */}
        <div className="absolute inset-0">
          <img 
            src={IMAGES.privateBeta} 
            alt="" 
            className="w-full h-full object-cover"
            style={{ objectPosition: '50% 40%' }}
          />
          {/* Gradient only at bottom for text */}
          <div className="absolute bottom-0 left-0 right-0 h-[35%] bg-gradient-to-t from-black via-black/80 to-transparent" />
        </div>
        
        {/* Text Block - AT VERY BOTTOM, compact */}
        <div className="absolute bottom-2 md:bottom-3 left-0 right-0 z-10 px-6 lg:px-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="max-w-2xl mx-auto text-center"
          >
            {/* LINE 1 - PRIMARY (Script C in DEEP GOLD + Serif) - BIGGEST */}
            <h2 className="leading-[1.1] mb-2 drop-shadow-lg">
              <span 
                className="inline-block mr-[-2px]"
                style={{ 
                  fontFamily: "'Pinyon Script', cursive",
                  fontSize: 'clamp(3rem, 6vw, 4.5rem)',
                  lineHeight: '0.8',
                  color: '#B8860B'
                }}
              >
                C
              </span>
              <span 
                className="text-white"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(1.25rem, 2.5vw, 1.8rem)',
                  fontWeight: '500'
                }}
              >
                et espace n'est pas pour tout le monde.
              </span>
            </h2>
            
            {/* LINE 2 - ITALIC, DEEP GOLD */}
            <p 
              className="mb-4 drop-shadow-md"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                fontStyle: 'italic',
                color: '#B8860B'
              }}
            >
              Et c'est intentionnel.
            </p>

            {/* Divider - DEEP GOLD */}
            <div className="w-12 h-[2px] mx-auto mb-4" style={{ backgroundColor: '#B8860B' }} />
            
            {/* PARAGRAPH - Normal serif, strong white, tighter spacing */}
            <div 
              className="space-y-2 mb-3"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)',
                lineHeight: '1.9'
              }}
            >
              <p className="text-white drop-shadow-md">
                Clierxter Vision Lab est une expérience{' '}
                <span className="font-semibold">premium exclusive</span>.
              </p>
              <p className="text-white drop-shadow-md">
                Pas parce qu'elles avaient besoin de quelque chose de plus.
              </p>
              {/* KEY SENTENCE - Entire line in ITALIC, "s'aligner" in DEEP GOLD */}
              <p className="text-white drop-shadow-md italic">
                Mais parce qu'elles savaient que quelque chose devait{' '}
                <span style={{ color: '#B8860B', fontWeight: '500' }}>s'aligner</span>.
              </p>
            </div>

            {/* Divider - THICKER, DEEP GOLD */}
            <div className="w-12 h-[2px] mx-auto mb-2" style={{ backgroundColor: '#B8860B' }} />
            
            {/* FINAL LINE - Italic, DEEP GOLD, bigger */}
            <p 
              className="drop-shadow-lg"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(1.15rem, 2.3vw, 1.4rem)',
                fontWeight: '500',
                fontStyle: 'italic',
                color: '#B8860B'
              }}
            >
              Il s'agit de devenir.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-44 bg-[#030303]">
        <div className="max-w-xl mx-auto px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p 
              className="text-white mb-6 leading-relaxed tracking-wide"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(1.3rem, 2.5vw, 1.6rem)',
                fontWeight: '500'
              }}
            >
              {t('cta.opening')}
            </p>
            
            {/* KEY EMOTIONAL LINE - More visible */}
            <p 
              className="mb-10 leading-relaxed"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(1rem, 1.8vw, 1.2rem)',
                fontStyle: 'italic',
                color: '#B8860B'
              }}
            >
              C'est un commencement intentionnel.
            </p>
            
            {/* Beta Spots Counter */}
            <div className="mb-10 flex justify-center">
              <BetaSpotsCounter variant="default" showBenefits={true} />
            </div>
            
            <Button 
              onClick={() => navigate(user ? '/dashboard' : '/auth')}
              className="bg-[#C9A227] text-black hover:bg-[#D4AF37] px-16 py-6 tracking-[0.2em] uppercase font-semibold transition-all duration-500 shadow-lg hover:shadow-xl"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(0.7rem, 1vw, 0.8rem)'
              }}
              data-testid="final-cta"
            >
              {t('hero.cta')}
            </Button>
            
            <p className="text-[#404040] text-[10px] mt-10 tracking-[0.25em] uppercase">
              {t('hero.limited')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-14 border-t border-[#0A0A0A] bg-[#030303]">
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_brand-alchemy-2/artifacts/3hu8wfzt_24FA2A63-8890-4D16-8044-60FA948D15B0.png" 
                alt="Clierxter Vision Lab" 
                className="h-10 w-auto object-contain opacity-70"
              />
            </div>
            <div className="flex items-center gap-6 flex-wrap justify-center">
              {SHOW_PRICING && <Link to="/pricing" className="text-[#505050] hover:text-[#D4AF37] text-[9px] tracking-[0.15em] uppercase transition-colors">{t('nav.pricing')}</Link>}
              <Link to="/auth" className="text-[#505050] hover:text-[#D4AF37] text-[9px] tracking-[0.15em] uppercase transition-colors">{t('nav.signIn')}</Link>
              <span className="text-[#252525]">|</span>
              <Link to="/cgv" className="text-[#404040] hover:text-[#D4AF37] text-[9px] tracking-[0.1em] uppercase transition-colors">CGV</Link>
              <Link to="/cgu" className="text-[#404040] hover:text-[#D4AF37] text-[9px] tracking-[0.1em] uppercase transition-colors">CGU</Link>
              <Link to="/confidentialite" className="text-[#404040] hover:text-[#D4AF37] text-[9px] tracking-[0.1em] uppercase transition-colors">Confidentialité</Link>
              <Link to="/mentions-legales" className="text-[#404040] hover:text-[#D4AF37] text-[9px] tracking-[0.1em] uppercase transition-colors">Mentions légales</Link>
            </div>
            <p className="text-[#252525] text-[9px]">© 2026 Clierxter</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
